/*
 * Copyright (c) 2016-present, Parse, LLC
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */
import Popover from 'components/Popover/Popover.react';
import PropTypes from 'prop-types';
import Position from 'lib/Position';
import React from 'react';
import ReactDOM from 'react-dom';
import styles from 'components/ChromeDropdown/ChromeDropdown.scss';

import Tooltip from 'rc-tooltip';

export default class ChromeDropdown extends React.Component {
  constructor() {
    super();

    this.state = {
      open: false,
      selected: false,
      searchString: '',
      searchTimer: null
    };

    this.handleKeyPress = this.handleKeyPress.bind(this);
  }

  componentDidMount() {
    this.node = ReactDOM.findDOMNode(this);

    this.keyValueMap = {};
    this.keyMap = {};

    this.optionRefs = new Map();

    this.props.options.forEach((item) => {
      if (item instanceof Object) {
        this.keyValueMap[item.key] = item.value;
        this.keyMap[item.key] = item;
      }
    });
    if (Object.keys(this.keyValueMap).length === 0) {
      this.keyValueMap = null;
    }

    if (this.state.open) {
      document.addEventListener('keydown', this.handleKeyPress);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyPress);
    if (this.state.searchTimer) {
      clearTimeout(this.state.searchTimer);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.open !== prevState.open) {
      if (this.state.open) {
        document.addEventListener('keydown', this.handleKeyPress);
      } else {
        document.removeEventListener('keydown', this.handleKeyPress);
      }
    }
  }

  handleKeyPress(e) {
    if (!this.state.open) return;

    if (this.state.searchTimer) {
      clearTimeout(this.state.searchTimer);
    }

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();

      const options = Array.from(this.optionRefs.values()).filter(el => el);
      const currentIndex = options.findIndex(el => el.classList.contains(styles.selected));

      let nextIndex;
      if (currentIndex === -1) {
        nextIndex = 0;
      } else if (e.key === 'ArrowUp') {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
      } else {
        nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
      }

      options.forEach(el => el.classList.remove(styles.selected));
      options[nextIndex].classList.add(styles.selected);
      options[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    if (e.key === 'Enter') {
      const selectedEl = Array.from(this.optionRefs.values()).find(el =>
        el && el.classList.contains(styles.selected)
      );

      if (selectedEl) {
        const key = selectedEl.getAttribute('data-key');
        if (key) {
          this.select(key, e);
          return;
        }
      }
    }

    const newSearchString = this.state.searchString + e.key.toLowerCase();

    const matchingOption = this.props.options.find(option => {
      const value = option instanceof Object ? option.value : option;
      return value.toLowerCase().startsWith(newSearchString);
    });

    if (matchingOption) {
      const key = matchingOption instanceof Object ? matchingOption.key : matchingOption;
      const optionEl = this.optionRefs.get(key);

      this.optionRefs.forEach((el) => {
        el.classList.remove(styles.selected);
      });

      if (optionEl) {
        optionEl.classList.add(styles.selected);
        optionEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    this.setState({
      searchString: newSearchString,
      searchTimer: setTimeout(() => {
        this.setState({ searchString: '' });
      }, 1000)
    });
  }

  select(value, e) {
    e.stopPropagation();
    this.setState({
      open: false,
      selected: true,
    }, () => {
      this.props.onChange(value);
    });
  }

  render() {
    let classes = [styles.dropdown];

    if (this.props.className) {
      classes.push(styles[this.props.className]);
    }

    let customStyle = {
      width: this.props.width || null,
      maxHeight: this.props.maxHeight || null
    };
    let color = this.props.color || 'purple';

    let label = this.props.value;
    if (this.keyValueMap) {
      label = this.keyValueMap[label];
    }

    let value_after = ((this.keyMap || {})[this.props.value] || {}).value_after

    if (!this.state.selected && this.props.placeholder) {
      label = this.props.placeholder;
    }
    let content = (
      <div className={[styles.current, styles[color]].join(' ')} onClick={() => this.setState({ open: true })}>
        {
          this.props.available && this.state.selected
            ?
            <div>
              <span className={styles.title}>{label}{value_after ? ` - ${value_after}` : ''}</span>
            </div>
            : <div>{label}</div>
        }
      </div>
    );

    if (this.state.open) {
      let position = Position.inWindow(this.node);
      let measuredWidth = this.node.offsetWidth;

      let menuStyle = {
        ...customStyle,
        width: measuredWidth
      };

      content = (
        <Popover fixed={true} position={position} onExternalClick={() => this.setState({ open: false })}>
          <div
            style={menuStyle}
            className={[
              styles.menu,
              this.props.className ? styles[this.props.className] : null,
              styles[color]
            ].join(' ')}
          >
            {this.props.options.map((o) => {
              let value_after;

              let key = o;
              let value = o;
              if (o instanceof Object) {
                key = o.key;
                value = o.value;
                value_after = o.value_after
              }

              if (this.props.available) {
                if (this.props.available.indexOf(key) > -1) {
                  return (
                    <Tooltip key={key} placement="right" overlay={'Available'} arrowContent={<div className="rc-tooltip-arrow-inner"></div>}>
                      <div onClick={this.select.bind(this, key)} ref={el => this.optionRefs.set(key, el)}>
                        <span className={styles.title}>{value}</span>
                        {value_after ? <strong>{value_after}</strong> : null}
                        <span className={styles.dot}></span>
                      </div>
                    </Tooltip>
                  );
                } else {
                  return (
                    <Tooltip key={key} placement="right" overlay={'Not available yet'} arrowContent={<div className="rc-tooltip-arrow-inner"></div>}>
                      <div
                        className={styles.notAvailable}
                        ref={el => this.optionRefs.set(key, el)}
                      >
                        <span className={styles.title}>{value}</span>
                        <strong style={{ fontSize: 12 }}>contact us</strong>
                        <span className={styles.dot}></span>
                      </div>
                    </Tooltip>
                  );
                }
              } else {
                return (
                  <div
                    key={key}
                    data-key={key}
                    ref={el => this.optionRefs.set(key, el)}
                    onClick={this.select.bind(this, key)}
                    onMouseEnter={(e) => {
                      this.optionRefs.forEach((el) => {
                        el.classList.remove(styles.selected);
                      });
                      e.currentTarget.classList.add(styles.selected);
                    }}
                  >
                    {value}
                  </div>
                );
              }
            })}
          </div>
        </Popover>
      );
    }

    return (
      <div style={customStyle} className={classes.join(' ')}>
        {content}
        {this.props.label ? (
          <label>
            {this.props.require ? <span>* </span> : null}
            {this.props.label}
          </label>
        ) : null}
      </div>
    );
  }
}

ChromeDropdown.propTypes = {
  color: PropTypes.oneOf(['blue', 'purple']),
  value: PropTypes.string,
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  width: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
};
